import { useState, useCallback, useMemo, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import Cookie from "universal-cookie";
import { useSearchParams } from "react-router-dom";
import { pay_with_stripe } from "../../../../api/calls/payment-engine/pay_with_stripe";
import { transaction_status } from "../../../../api/calls/payment-engine/transaction_status";
import { order_list } from "../../../../api/calls/order/order_list";
import { update_delivery_data } from "../../../../api/calls/basket/update_delivery_data";
import { get_user_details } from "../../../../api/calls/user/get_user_details";
import { setMainModal } from "../../../../reducers/slices/main";
import { hasDearCustomerLink } from "../../../../helpers/dearCustomerLink";
const cookie = new Cookie();
const CLIENT_ID = process.env.LETS_TRADE_CLIENT_ID ?? "";
export function useCheckoutState() {
    const dispatch = useDispatch();
    const { basket } = useSelector((state) => state.basketState);
    const { address, selection } = useSelector((state) => state.selectionState);
    const { selectedShipping } = selection || {};
    const userId = useSelector((state) => state.userState?.user?.user?._id);
    const [checkoutIsLoading, setCheckoutIsLoading] = useState(false);
    const [secretKey, setSecretKey] = useState("");
    const [channels, setChannels] = useState(undefined);
    const [channelsLoading, setChannelsLoading] = useState(false);
    const [searchParams] = useSearchParams();
    const transactionCompleted = searchParams.get("completed");
    const [transactionId, setTransactionId] = useState("");
    const [loadingTransactionStatus, setLoadingTransactionStatus] = useState(false);
    const [transactionStatus, setTransactionStatus] = useState("");
    const [order, setOrder] = useState(null);
    const [instructions, setInstructions] = useState("");
    useEffect(() => {
        if (transactionCompleted) {
            const transactionId = cookie.get("transaction_id");
            setTransactionId(transactionId);
        }
    }, [transactionCompleted]);
    // Pull channels off the user record so we can verify the DEAR customer link
    // before letting the user pay. JWT-decoded user (sign-in) doesn't carry channels.
    useEffect(() => {
        if (!userId) {
            setChannels(undefined);
            return;
        }
        setChannelsLoading(true);
        get_user_details({ _id: userId, client_id: CLIENT_ID })
            .then((response) => {
            if (response?.success)
                setChannels(response.content?.channels ?? []);
            else
                setChannels([]);
        })
            .catch(() => setChannels([]))
            .finally(() => setChannelsLoading(false));
    }, [userId]);
    const canPlaceOrder = useMemo(() => hasDearCustomerLink(channels), [channels]);
    // Only flag the missing link once channels have actually loaded — avoids a brief
    // "not linked" flash while the fetch is in flight.
    const showAccountNotLinked = useMemo(() => !canPlaceOrder && !channelsLoading && channels !== undefined, [canPlaceOrder, channelsLoading, channels]);
    useEffect(() => {
        if (transactionId) {
            setLoadingTransactionStatus(true);
            transaction_status({ id: transactionId }).then(async (response) => {
                if (response.success) {
                    const ordersResponse = await order_list({ page: "1", size: "10", status: "ORDERED" });
                    const { success: orderResponseSuccess, content } = ordersResponse;
                    if (orderResponseSuccess) {
                        const { order_list } = content;
                        // Find order that matches the transactionId.
                        const order = order_list.find((order) => order?.transaction_details?.transaction?._id === transactionId);
                        if (order) {
                            setOrder(order);
                        }
                        else {
                            dispatch(setMainModal({ title: "Order not found", messages: ["We could not find your order. Please contact support."] }));
                        }
                    }
                    const { status } = response?.content;
                    setTransactionStatus(status);
                    setLoadingTransactionStatus(false);
                }
                else {
                    setLoadingTransactionStatus(false);
                    dispatch(setMainModal({ title: "Transaction Status Error", messages: [response.message] }));
                }
                cookie.remove("transaction_id");
            });
        }
    }, [transactionId]);
    const handleCheckout = useCallback(async (data) => {
        if (!address) {
            dispatch(setMainModal({
                title: "No address selected",
                messages: [
                    "Please select an address before proceeding to checkout.",
                    "If you do not have an address, please add an address on the Checkout or Account Details pages.",
                ],
            }));
            return;
        }
        if (selectedShipping == null) {
            dispatch(setMainModal({
                title: "No shipping option selected",
                messages: ["Please select a shipping option before proceeding to checkout."],
            }));
            return;
        }
        setCheckoutIsLoading(true);
        const request = {
            _id: basket?._id ?? "",
            basket: {
                type: "delivery",
                provider: basket?.provider,
                instructions: instructions,
                address: {
                    full_address: address?.full_address ?? "",
                    latitude: address?.latitude?.toString() ?? "",
                    longitude: address?.longitude?.toString() ?? "",
                },
            },
        };
        const response = await update_delivery_data(request);
        if (!response.success) {
            setCheckoutIsLoading(false);
            dispatch(setMainModal({ title: "Something went wrong!", messages: [response.message] }));
            return;
        }
        // Check if the basket response has any vouchers that need to be applied before checkout.
        const { vouchers } = response.content;
        const vouchersToApply = vouchers?.filter((voucher) => voucher.applied === true).map((voucher) => voucher.voucher_code) ?? [];
        const { basket_id } = data;
        const url = window.location.origin;
        pay_with_stripe({
            basket_id: basket_id,
            success_url: `${url}/#/checkout?completed=true`,
            ...(vouchersToApply.length > 0 && { vouchers: vouchersToApply }),
        })
            .then((response) => {
            setCheckoutIsLoading(false);
            if (response.success) {
                const transactionId = response.content?.transaction_id ?? "";
                const paymentUrl = response.content?.session_url ?? "";
                const cookie = new Cookie();
                cookie.set("transaction_id", transactionId, {
                    sameSite: true,
                });
                window.location.href = paymentUrl;
                setSecretKey(secretKey);
            }
            else {
                dispatch(setMainModal({
                    title: "Could not checkout order",
                    messages: [response.message],
                }));
            }
        })
            .catch((error) => {
            setCheckoutIsLoading(false);
            dispatch(setMainModal({
                title: "Could not checkout order",
                messages: [error.message],
            }));
        });
    }, [basket, address, instructions, selectedShipping]);
    return useMemo(() => ({
        handleCheckout,
        checkoutIsLoading,
        secretKey,
        loadingTransactionStatus,
        transactionStatus,
        order,
        setInstructions,
        instructions,
        canPlaceOrder,
        showAccountNotLinked,
    }), [
        handleCheckout,
        checkoutIsLoading,
        secretKey,
        loadingTransactionStatus,
        transactionStatus,
        order,
        setInstructions,
        instructions,
        canPlaceOrder,
        showAccountNotLinked,
    ]);
}
